import { useContext, useEffect, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import Color from "color";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { userHasPermission } from "@/modules/common/utils";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import EditAssignedUser from "@/modules/lead-page/components/lead-info/edit-fields/assigned-user";
import LeadInfoField from "@/modules/lead-page/new/components/lead-info-field";
import useLeadInfo from "@/modules/lead-page/hooks/lead-info/lead-info.hook";
import { CommonAvatar } from "@/modules/common/components/ui-kit/common-avatar";
import FieldWrapper from "@/modules/lead-page/new/components/sidebar/fields/field-wrapper";
import useGetLeadSales from "@/modules/lead-page/hooks/get-lead-sales";
import SalesProgressWarning from "@/modules/common/components/ui-kit/sales-progress-warning";

const PROGRESS_STATUS_TYPES = ["First Serious Action", "Contracted"];

const AssignedUserField = () => {
  const theme = useTheme();
  const { assignedUser, lead } = useLeadInfo();
  const { leadSales } = useGetLeadSales();
  const hasProgressStatus =
    !!lead?.data?.lead?.status?.statusType &&
    PROGRESS_STATUS_TYPES.includes(lead.data.lead.status.statusType);
  const hasSalesProgress = hasProgressStatus || leadSales.length > 0;
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  const [isTouchDevice, setIsTouchDevice] = useState(false);

  useEffect(() => {
    const isTouchScreen =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;
    setIsTouchDevice(isTouchScreen);
  }, []);

  const renderPlaceholder = () => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          cursor:
            userHasPermission({
              name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
              level: PermissionLevel.WRITE,
            }) && !isTouchDevice
              ? "pointer"
              : "default",
          "&:hover": {
            opacity: userHasPermission({
              name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
              level: PermissionLevel.WRITE,
            })
              ? 0.8
              : 1,
          },
        }}
      >
        <CommonAvatar />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
          }}
        >
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.md.regular,
              color: theme.nawy.colors.semantic.text.primary,
            }}
          >
            Unassigned
          </Typography>
        </Box>
      </Box>
    );
  };

  const renderAssignedUserContent = () => {
    if (!lead?.data?.user_name) {
      return renderPlaceholder();
    }

    const userColor = lead?.data?.new_user_color || lead?.data?.user_color;
    const colorObj = userColor ? new Color(userColor) : null;

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <CommonAvatar name={lead?.data?.user_name} color={userColor} />
        <Typography
          data-testid="lead-assigned-agent"
          sx={{
            ...theme.nawy.typography.english.variants.body.md.regular,
            color: colorObj
              ? colorObj.darken(0.5).toString()
              : theme.nawy.colors.semantic.text.primary,
            "&:hover": {
              textDecoration: userHasPermission(
                {
                  name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
                  level: PermissionLevel.WRITE,
                },
                currentUserPermissions
              )
                ? "underline"
                : "none",
            },
            cursor:
              userHasPermission({
                name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
                level: PermissionLevel.WRITE,
              }) && !isTouchDevice
                ? "pointer"
                : "default",
          }}
        >
          {lead?.data?.user_name}
        </Typography>
      </Box>
    );
  };

  return (
    <LeadInfoField
      editModalOpen={assignedUser.editAssignedUserModalOpen}
      setEditModalOpen={assignedUser.setEditAssignedUserModalOpen}
      editable={userHasPermission(
        {
          name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
          level: PermissionLevel.WRITE,
        },
        currentUserPermissions
      )}
      content={renderAssignedUserContent()}
      editModal={
        <PermissionGuard
          permissions={[
            {
              name: PermissionsEnum.JOURNEY_ASSIGNED_USER,
              level: PermissionLevel.WRITE,
            },
          ]}
        >
          <ResponsiveModal
            open={assignedUser.editAssignedUserModalOpen}
            onClose={() => assignedUser.setEditAssignedUserModalOpen(false)}
            title="Edit Assigned User"
            confirmButtonText="Save"
            cancelButtonText="Cancel"
            onConfirm={() => assignedUser.handleAssignLead()}
            isLoading={assignedUser.isLoading}
            isConfirmDisabled={!assignedUser.state}
            snapPoints={[1]}
          >
            <FieldWrapper>
              <EditAssignedUser assignedUser={assignedUser} lead={lead} />
            </FieldWrapper>
            {hasSalesProgress && (
              <Box
                sx={{
                  marginTop: theme.nawy.spacing.md,
                  padding: `0 ${theme.nawy.spacing.md}`,
                }}
              >
                <SalesProgressWarning message="This lead already has a sale or first action (EOI, Reserved, or Blocked). Reassigning may cause confusion with the client." />
              </Box>
            )}
          </ResponsiveModal>
        </PermissionGuard>
      }
    />
  );
};

export default AssignedUserField;
